class UniCarouselDescription extends HTMLElement {
  constructor() {
    super();

    this.swiper = null;
    this.onPrev = null;
    this.onNext = null;
    this.onKeydown = null;
    this.rafId = null;
    this.swiperEl = null;
    this.btnPrev = null;
    this.btnNext = null;

    this._updateNav = null;
    this._swiperEvents = [
      "init",
      "slideChange",
      "reachBeginning",
      "reachEnd",
      "fromEdge",
      "lock",
      "unlock",
      "resize",
      "observerUpdate",
      "breakpoint",
    ];
  }

  connectedCallback() {
    const boot = () => {
      const hasSwiper = window.FoxTheme && window.FoxTheme.Swiper && window.FoxTheme.Swiper.Swiper;

      if (!hasSwiper) {
        this.rafId = requestAnimationFrame(boot);
        return;
      }

      // Evita doppie inizializzazioni
      if (this.swiper) return;

      this.swiperEl = this.querySelector("[data-swiper]");
      if (!this.swiperEl) return;

      this.btnPrev = this.querySelector('[data-navigation="prev"]');
      this.btnNext = this.querySelector('[data-navigation="next"]');

      this.swiper = new window.FoxTheme.Swiper.Swiper(this.swiperEl, {
        // Mobile default: 1 e un pezzo
        slidesPerView: 1.2,
        spaceBetween: 16,
        speed: 500,
        grabCursor: true,
        watchOverflow: true, // abilita isLocked quando non c'è overflow
        // Reagisci ai cambi di dimensione/DOM della grid
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        // Desktop: 2 e un pezzo
        breakpoints: {
          480: { slidesPerView: 1.25, spaceBetween: 18 },
          700: { slidesPerView: 1.35, spaceBetween: 20 },
          1024: { slidesPerView: 2.2, spaceBetween: 24 },
          1280: { slidesPerView: 2.5, spaceBetween: 24 },
        },
      });

      // Funzione che abilita/disabilita i bottoni in base allo stato dello swiper
      this._updateNav = () => {
        if (!this.btnPrev || !this.btnNext || !this.swiper) return;

        const locked = !!this.swiper.isLocked; // true se non c'è overflow (watchOverflow)
        const atStart = !!this.swiper.isBeginning; // true alla prima slide
        const atEnd = !!this.swiper.isEnd; // true all'ultima slide

        const disablePrev = locked || atStart;
        const disableNext = locked || atEnd;

        this.btnPrev.toggleAttribute("disabled", disablePrev);
        this.btnPrev.setAttribute("aria-disabled", String(disablePrev));

        this.btnNext.toggleAttribute("disabled", disableNext);
        this.btnNext.setAttribute("aria-disabled", String(disableNext));
      };

      // Listeners bottoni (rispettano lo stato disabilitato)
      this.onPrev = () => {
        if (!this.btnPrev?.hasAttribute("disabled")) this.swiper?.slidePrev();
      };
      this.onNext = () => {
        if (!this.btnNext?.hasAttribute("disabled")) this.swiper?.slideNext();
      };
      this.btnPrev && this.btnPrev.addEventListener("click", this.onPrev);
      this.btnNext && this.btnNext.addEventListener("click", this.onNext);

      // Rendi il componente focusable per far funzionare il keydown
      if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
      this.onKeydown = (e) => {
        if (e.key === "ArrowLeft") this.onPrev();
        if (e.key === "ArrowRight") this.onNext();
      };
      this.addEventListener("keydown", this.onKeydown);

      // Aggiorna lo stato nav su tutti gli eventi rilevanti di Swiper
      this._swiperEvents.forEach((evt) => this.swiper.on(evt, this._updateNav));

      // Stato iniziale
      this._updateNav();
    };

    boot();
  }

  disconnectedCallback() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Rimuovi listeners bottoni e tastiera
    if (this.btnPrev && this.onPrev) this.btnPrev.removeEventListener("click", this.onPrev);
    if (this.btnNext && this.onNext) this.btnNext.removeEventListener("click", this.onNext);
    if (this.onKeydown) this.removeEventListener("keydown", this.onKeydown);
    this.onPrev = this.onNext = this.onKeydown = null;

    // Stacca gli eventi da Swiper
    if (this.swiper && this._updateNav) {
      this._swiperEvents.forEach((evt) => this.swiper.off(evt, this._updateNav));
    }
    this._updateNav = null;

    // Distruggi Swiper per non lasciare istanze in giro
    if (this.swiper && typeof this.swiper.destroy === "function") {
      this.swiper.destroy(true, true);
    }

    this.swiper = null;
  }
}

if (!customElements.get("uni-carousel-description")) {
  customElements.define("uni-carousel-description", UniCarouselDescription);
}
